/**
 * Device operational events — tablet → backend upstream channel.
 *
 * `reportDeviceEvent(type, payload)` publishes to the device's own
 * `device-events/{deviceId}` channel; an AppSync handler forwards it to the
 * bronze-device-events EventBridge bus (→ CloudWatch Logs + per-type metric).
 *
 * This is a telemetry side-channel: it must NEVER interfere with the app.
 * Every publish is fire-and-forget, errors are swallowed, and a per-type
 * throttle (1/min) guarantees a stuck failure loop can't flood the bus.
 *
 * Part-1 types: camera_error, upload_stuck, apply_failed. New types need no
 * backend change (the handler normalizes any type; reaction rules are added
 * on the bus when a concrete need appears).
 */
import { events } from 'aws-amplify/data';
import Logger from '@/logger';
import { liveDemoOn } from '@/liveDemo/flag';

const logger = new Logger({ screen: 'deviceEvents' });

const THROTTLE_MS = 60000; // max 1 report per type per minute

let deviceId = null;
let restaurantId = null;
const lastSentByType = new Map();

export function initDeviceEvents(ids) {
  deviceId = ids.deviceId;
  restaurantId = ids.restaurantId;
  lastSentByType.clear();
}

export function stopDeviceEvents() {
  deviceId = null;
  restaurantId = null;
  lastSentByType.clear();
}

export function reportDeviceEvent(type, payload = {}, severity = 'warning') {
  if (!deviceId || !type) return;
  // Live sales demo on: nothing the presenter does is telemetry (src/liveDemo/).
  if (liveDemoOn()) return;

  const last = lastSentByType.get(type) || 0;
  if (Date.now() - last < THROTTLE_MS) return;
  lastSentByType.set(type, Date.now());

  events.post(`device-events/${deviceId}`, {
    type,
    severity,
    restaurant_id: restaurantId,
    ts: new Date().toISOString(),
    payload,
  }).catch((error) => {
    // Telemetry only — log locally and move on.
    logger.console(`Could not report device event '${type}'`, error);
  });
}
